import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { noFutureYearValidator } from '@edtech-ui/core/utils/noFutureYearValidator';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-admin-course-creation-add-resources',
  standalone: true,
  imports: [MatIconModule, ReactiveFormsModule, CommonModule],
  templateUrl: './admin-course-creation-add-resources.component.html',
  styleUrl: './admin-course-creation-add-resources.component.scss'
})
export class AdminCourseCreationAddResourcesComponent implements OnInit {

  resourcesForm!: FormGroup;
  textbookForm!: FormGroup;
  addTextbookVisible = false;
  textbooks: any[] = [];
  webResourceForm!: FormGroup;
  addWebResourceVisible = false;
  webResources: string[] = [];
  uploadedFile: File | null = null;
  uploadedFileSize = '';
  private readonly MAX_FILE_SIZE = 20 * 1024 * 1024;
  classroomId = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private courseService: CourseManagementService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.route.parent?.paramMap.subscribe(params => {
      this.classroomId = params.get('classroomId') ?? '';
    });

    this.resourcesForm = this.fb.group({});

    this.textbookForm = this.fb.group({
      title: ['', [Validators.required, Validators.pattern(/^(?=.*[A-Za-z])[A-Za-z0-9 .,;&'-]+$/)]],
      author: ['', [Validators.required, Validators.pattern(/^(?=.*[A-Za-z])[A-Za-z0-9 .,;&'-]+$/)]],
      publisher: ['', [Validators.required, Validators.pattern(/^(?=.*[A-Za-z])[A-Za-z0-9 .,;&'-]+$/)]],
      year: ['', [Validators.required, Validators.pattern(/^\d{4}$/), noFutureYearValidator]],
    });

    this.webResourceForm = this.fb.group({
      url: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]],
    });

    const payload = this.courseService.getPayload();
    this.textbooks = payload.textbooks || [];
    this.webResources = payload.web_references_links || [];
  }


  toggleAddTextbook() {
    this.addTextbookVisible = !this.addTextbookVisible;
  }

  cancelAddTextbook() {
    this.textbookForm.reset();
    this.addTextbookVisible = false;
  }

  saveTextbook() {
    if (this.textbookForm.invalid) {
      this.textbookForm.markAllAsTouched();
      this.toastr.warning(
        'Please complete all required fields with valid details.',
        'Invalid Data'
      );
      return;
    }

    this.textbooks.push(this.textbookForm.value);
    this.courseService.updatePayload({ textbooks: this.textbooks });

    this.textbookForm.reset();
    this.addTextbookVisible = false;
  }

  editTextbook(index: number) {
    this.textbookForm.patchValue(this.textbooks[index]);
    this.textbooks.splice(index, 1);
    this.courseService.updatePayload({ textbooks: this.textbooks });
    this.addTextbookVisible = true;
  }

  deleteTextbook(index: number) {
    this.textbooks.splice(index, 1);
    this.courseService.updatePayload({ textbooks: this.textbooks });
    this.toastr.info('Textbook deleted successfully');
  }


  toggleAddWebResource() {
    this.addWebResourceVisible = !this.addWebResourceVisible;
  }

  cancelAddWebResource() {
    this.webResourceForm.reset();
    this.addWebResourceVisible = false;
  }

  saveWebResource() {
    if (this.webResourceForm.invalid) {
      this.webResourceForm.markAllAsTouched();
      this.toastr.warning(
        'Please enter a valid URL starting with http:// or https://',
        'Invalid URL'
      );
      return;
    }

    this.webResources.push(this.webResourceForm.value.url);
    this.courseService.updatePayload({ web_references_links: this.webResources });

    this.webResourceForm.reset();
    this.addWebResourceVisible = false;
  }

  editWebResource(index: number) {
    this.webResourceForm.patchValue({ url: this.webResources[index] });
    this.webResources.splice(index, 1);
    this.courseService.updatePayload({ web_references_links: this.webResources });
    this.addWebResourceVisible = true;
  }

  deleteWebResource(index: number) {
    this.webResources.splice(index, 1);
    this.courseService.updatePayload({ web_references_links: this.webResources });
    this.toastr.info('Web Resource deleted successfully');
  }
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];

    if (file.size > this.MAX_FILE_SIZE) {
      this.toastr.error('File size exceeds 20 MB');

      input.value = '';
      this.uploadedFile = null;
      this.uploadedFileSize = '';
      this.courseService.updatePayload({ course_document: null });

      return;
    }

    this.uploadedFile = file;
    this.uploadedFileSize = this.formatFileSize(file.size);

    this.courseService.updatePayload({
      course_document: file
    });
  }


  removeFile(): void {
    this.uploadedFile = null;
    this.uploadedFileSize = '';
    this.courseService.updatePayload({ course_document: null });
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  goToPrePage() {
    this.router.navigate(['/admin-student-platform/course-setup/outcomes']);
  }

  goToNextPage() {
    this.router.navigate(['/admin-student-platform/preview']);
  }
}
